import {
    CheckCircle,
    ContentCopy,
    Info,
    KeyboardArrowDown,
    KeyboardArrowUp,
    Terminal,
} from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Card,
    Chip,
    Collapse,
    Container,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PRICING, ROUTES } from '@/config/app.config';
import { useApiKeysStore } from '@/stores/use-api-keys-store';
import { useAuthStore } from '@/stores/use-auth-store';
import { useNotificationStore } from '@/stores/use-notification-store';
import { useSapEnvironmentsStore } from '@/stores/use-sap-environments-store';
import { useThemeStore } from '@/stores/use-theme-store';

const MCP_SERVER_URL = 'https://abap-mcp.mcps.b2rise.com/mcp';
const WS_CLIENT_PACKAGE = '@numends/abap-client';

type IdeType = 'cursor' | 'vscode' | 'windsurf' | 'claude-desktop';

interface IdeConfig {
    label: string;
    filePath: string;
    buildConfig: (apiKey: string) => object;
}

const IDE_CONFIGS: Record<IdeType, IdeConfig> = {
    cursor: {
        label: 'Cursor',
        filePath: '~/.cursor/mcp.json',
        buildConfig: (apiKey) => ({
            mcpServers: {
                'abap-mcp': {
                    url: MCP_SERVER_URL,
                    headers: { 'X-API-Key': apiKey },
                },
            },
        }),
    },
    vscode: {
        label: 'VS Code',
        filePath: '.vscode/mcp.json',
        buildConfig: (apiKey) => ({
            servers: {
                'abap-mcp': {
                    type: 'http',
                    url: MCP_SERVER_URL,
                    headers: { 'X-API-Key': apiKey },
                },
            },
        }),
    },
    windsurf: {
        label: 'Windsurf',
        filePath: '~/.codeium/windsurf/mcp_config.json',
        buildConfig: (apiKey) => ({
            mcpServers: {
                'abap-mcp': {
                    serverUrl: MCP_SERVER_URL,
                    headers: { 'X-API-Key': apiKey },
                },
            },
        }),
    },
    'claude-desktop': {
        label: 'Claude Desktop',
        filePath: '~/.config/Claude/claude_desktop_config.json',
        buildConfig: (apiKey) => ({
            mcpServers: {
                'abap-mcp': {
                    command: 'npx',
                    args: [
                        'mcp-remote',
                        MCP_SERVER_URL,
                        '--header',
                        `X-API-Key: ${apiKey}`,
                    ],
                },
            },
        }),
    },
};

function CodeBlock({ code, onCopy }: { code: string; onCopy: (text: string) => void }) {
    const { mode } = useThemeStore();

    return (
        <Box
            sx={{
                position: 'relative',
                mt: 2,
                borderRadius: 2,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    px: 2,
                    py: 0.5,
                    bgcolor: mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
                    borderBottom: '1px solid',
                    borderColor: mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
                }}
            >
                <Typography variant="caption" color="text.secondary">
                    JSON
                </Typography>
                <Tooltip title="Copy">
                    <IconButton size="small" onClick={() => onCopy(code)}>
                        <ContentCopy fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>
            <Box
                component="pre"
                sx={{
                    m: 0,
                    p: 2,
                    overflow: 'auto',
                    bgcolor: mode === 'dark' ? '#0D0F1F' : '#F8F9FA',
                    fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                    fontSize: '0.8rem',
                    lineHeight: 1.6,
                    color: mode === 'dark' ? '#E2E8F0' : '#1F2937',
                }}
            >
                {code}
            </Box>
        </Box>
    );
}

function TerminalBlock({ command, onCopy }: { command: string; onCopy: (text: string) => void }) {
    const { mode } = useThemeStore();

    return (
        <Box
            sx={{
                position: 'relative',
                mt: 2,
                borderRadius: 2,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    px: 2,
                    py: 0.5,
                    bgcolor: mode === 'dark' ? '#1A1A2E' : '#2D3748',
                    borderBottom: '1px solid',
                    borderColor: mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.1)',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Terminal sx={{ fontSize: 14, color: '#A0AEC0' }} />
                    <Typography variant="caption" sx={{ color: '#A0AEC0' }}>
                        Terminal
                    </Typography>
                </Box>
                <Tooltip title="Copy">
                    <IconButton size="small" onClick={() => onCopy(command)} sx={{ color: '#A0AEC0' }}>
                        <ContentCopy fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>
            <Box
                component="pre"
                sx={{
                    m: 0,
                    p: 2,
                    overflow: 'auto',
                    bgcolor: mode === 'dark' ? '#0D0F1F' : '#1A202C',
                    fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                    fontSize: '0.8rem',
                    lineHeight: 1.6,
                    color: '#68D391',
                }}
            >
                <Box component="span" sx={{ color: '#A0AEC0' }}>
                    ${' '}
                </Box>
                {command}
            </Box>
        </Box>
    );
}

interface StepConfig {
    id: string;
    translationKey: string;
    isCompleted?: boolean;
}

export function SetupGuidePage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { user } = useAuthStore();
    const { fetchKeys, getActiveKeys } = useApiKeysStore();
    const { environments, fetchEnvironments } = useSapEnvironmentsStore();
    const { addNotification } = useNotificationStore();
    const { mode } = useThemeStore();

    const [expandedStep, setExpandedStep] = useState<number | null>(null);
    const [selectedKeyId, setSelectedKeyId] = useState<string>('');
    const [selectedIde, setSelectedIde] = useState<IdeType>('cursor');

    useEffect(() => {
        void fetchKeys();
        void fetchEnvironments();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const activeKeys = getActiveKeys();

    useEffect(() => {
        if (activeKeys.length > 0 && !selectedKeyId && activeKeys[0]) {
            setSelectedKeyId(activeKeys[0].id);
        }
    }, [activeKeys, selectedKeyId]);

    const selectedKey = useMemo(
        () => activeKeys.find((k) => k.id === selectedKeyId),
        [activeKeys, selectedKeyId],
    );

    if (!user) {
        return null;
    }

    const hasSubscription = user.subscriptions.length > 0;
    const hasEnvironments = environments.length > 0;
    const hasApiKey = activeKeys.length > 0;

    const steps: StepConfig[] = [
        { id: 'subscribe', translationKey: 'subscribe', isCompleted: hasSubscription },
        { id: 'sapEnvironments', translationKey: 'sapEnvironments', isCompleted: hasEnvironments },
        { id: 'apiKey', translationKey: 'apiKey', isCompleted: hasApiKey },
        { id: 'configureIde', translationKey: 'configureIde' },
        { id: 'websocketClient', translationKey: 'websocketClient' },
    ];

    const handleCopy = (text: string) => {
        void navigator.clipboard.writeText(text);
        addNotification(t('common.copied'), 'success');
    };

    const toggleStep = (index: number) => {
        setExpandedStep(expandedStep === index ? null : index);
    };

    const getPlanName = (planId: string) =>
        planId === 'mcp-abap' ? PRICING.plans.abap.name : PRICING.plans.btp.name;

    const ideConfig = IDE_CONFIGS[selectedIde];
    const apiKeyValue = selectedKey?.maskedKey ?? '<your-api-key>';
    const mcpJsonConfig = JSON.stringify(ideConfig.buildConfig(apiKeyValue), null, 4);
    const npxCommand = `npx ${WS_CLIENT_PACKAGE}`;

    const renderStepContent = (step: StepConfig) => {
        const key = step.translationKey;

        switch (step.id) {
            case 'subscribe':
                return (
                    <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {t(`setupGuide.steps.${key}.description`)}
                        </Typography>

                        {hasSubscription && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                {user.subscriptions.map((planId) => (
                                    <Chip
                                        key={planId}
                                        label={getPlanName(planId)}
                                        color="success"
                                        size="small"
                                        variant="outlined"
                                    />
                                ))}
                            </Box>
                        )}

                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => navigate(ROUTES.purchase)}
                            sx={{ borderRadius: 2 }}
                        >
                            {t(`setupGuide.steps.${key}.action`)}
                        </Button>
                    </Box>
                );

            case 'sapEnvironments':
                return (
                    <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {t(`setupGuide.steps.${key}.description`)}
                        </Typography>

                        {hasEnvironments && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                {environments.map((env) => (
                                    <Chip
                                        key={env.id}
                                        label={env.description}
                                        size="small"
                                        variant="outlined"
                                    />
                                ))}
                            </Box>
                        )}

                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => navigate(ROUTES.sapEnvironments)}
                            sx={{ borderRadius: 2 }}
                        >
                            {t(`setupGuide.steps.${key}.action`)}
                        </Button>
                    </Box>
                );

            case 'apiKey':
                return (
                    <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {t(`setupGuide.steps.${key}.description`)}
                        </Typography>

                        {hasApiKey && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                                {activeKeys.map((k) => (
                                    <Box
                                        key={k.id}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1.5,
                                            p: 1.5,
                                            borderRadius: 2,
                                            bgcolor: mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                                        }}
                                    >
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {k.name}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                fontFamily: '"JetBrains Mono", monospace',
                                                color: 'text.secondary',
                                            }}
                                        >
                                            {k.maskedKey}
                                        </Typography>
                                        <Chip
                                            label={getPlanName(k.planId)}
                                            size="small"
                                            variant="outlined"
                                            color="primary"
                                        />
                                    </Box>
                                ))}
                            </Box>
                        )}

                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => navigate(ROUTES.apiKeys)}
                            sx={{ borderRadius: 2 }}
                        >
                            {t(`setupGuide.steps.${key}.action`)}
                        </Button>
                    </Box>
                );

            case 'configureIde':
                return (
                    <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {t(`setupGuide.steps.${key}.description`)}
                        </Typography>

                        <ToggleButtonGroup
                            value={selectedIde}
                            exclusive
                            onChange={(_, value) => value && setSelectedIde(value as IdeType)}
                            size="small"
                            sx={{
                                mb: 2,
                                '& .MuiToggleButton-root': {
                                    borderRadius: '8px !important',
                                    px: 2,
                                    py: 0.75,
                                    fontWeight: 600,
                                    fontSize: '0.8rem',
                                    textTransform: 'none',
                                    border: '1px solid',
                                    borderColor: mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
                                    mx: 0.5,
                                    '&.Mui-selected': {
                                        bgcolor: 'primary.main',
                                        color: 'primary.contrastText',
                                        '&:hover': { bgcolor: 'primary.dark' },
                                    },
                                },
                            }}
                        >
                            {(Object.keys(IDE_CONFIGS) as IdeType[]).map((ide) => (
                                <ToggleButton key={ide} value={ide}>
                                    {IDE_CONFIGS[ide].label}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>

                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            {t(`setupGuide.steps.${key}.filePath`)}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                mb: 2,
                                fontFamily: '"JetBrains Mono", monospace',
                                fontSize: '0.8rem',
                                color: 'primary.main',
                            }}
                        >
                            {ideConfig.filePath}
                        </Typography>

                        {hasApiKey ? (
                            <>
                                {activeKeys.length > 1 && (
                                    <FormControl size="small" sx={{ mb: 1, minWidth: 250 }}>
                                        <InputLabel>{t('setupGuide.steps.configureIde.selectKey')}</InputLabel>
                                        <Select
                                            value={selectedKeyId}
                                            label={t('setupGuide.steps.configureIde.selectKey')}
                                            onChange={(e) => setSelectedKeyId(e.target.value)}
                                        >
                                            {activeKeys.map((k) => (
                                                <MenuItem key={k.id} value={k.id}>
                                                    {k.name} ({k.maskedKey})
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                )}
                                <CodeBlock code={mcpJsonConfig} onCopy={handleCopy} />

                                {selectedIde === 'claude-desktop' && (
                                    <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                                        {t(`setupGuide.steps.${key}.claudeNote`)}
                                    </Alert>
                                )}
                            </>
                        ) : (
                            <Alert severity="info" sx={{ borderRadius: 2 }}>
                                {t(`setupGuide.steps.${key}.noKeyWarning`)}
                            </Alert>
                        )}
                    </Box>
                );

            case 'websocketClient':
                return (
                    <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {t(`setupGuide.steps.${key}.description`)}
                        </Typography>

                        <TerminalBlock command={npxCommand} onCopy={handleCopy} />

                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                            {t(`setupGuide.steps.${key}.interactiveNote`)}
                        </Typography>

                        <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                {t(`setupGuide.steps.${key}.commands`)}
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <Typography variant="body2" component="code" sx={{ fontFamily: 'monospace' }}>
                                    npx @numends/abap-client
                                    <Typography component="span" variant="body2" color="text.secondary">
                                        {' — '}{t(`setupGuide.steps.${key}.commandStart`)}
                                    </Typography>
                                </Typography>
                                <Typography variant="body2" component="code" sx={{ fontFamily: 'monospace' }}>
                                    npx @numends/abap-client --setup
                                    <Typography component="span" variant="body2" color="text.secondary">
                                        {' — '}{t(`setupGuide.steps.${key}.commandSetup`)}
                                    </Typography>
                                </Typography>
                                <Typography variant="body2" component="code" sx={{ fontFamily: 'monospace' }}>
                                    npx @numends/abap-client --help
                                    <Typography component="span" variant="body2" color="text.secondary">
                                        {' — '}{t(`setupGuide.steps.${key}.commandHelp`)}
                                    </Typography>
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ mt: 3 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                {t(`setupGuide.steps.${key}.requirements`)}
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <Typography variant="body2" color="text.secondary">
                                    • {t(`setupGuide.steps.${key}.requirementNode`)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    • {t(`setupGuide.steps.${key}.requirementVpn`)}
                                </Typography>
                            </Box>
                        </Box>

                        <Alert severity="info" icon={<Info />} sx={{ mt: 2, borderRadius: 2 }}>
                            {t(`setupGuide.steps.${key}.tip`)}
                        </Alert>
                    </Box>
                );

            default:
                return null;
        }
    };

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                {t('setupGuide.title')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {t('setupGuide.subtitle')}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {steps.map((step, index) => (
                    <Card
                        key={step.id}
                        sx={{
                            border: '1px solid',
                            borderColor: mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                p: 2.5,
                                cursor: 'pointer',
                                '&:hover': {
                                    bgcolor: mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
                                },
                            }}
                            onClick={() => toggleStep(index)}
                        >
                            {step.isCompleted !== undefined && (
                                <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}>
                                    <CheckCircle
                                        sx={{
                                            fontSize: 20,
                                            color: step.isCompleted ? 'success.main' : 'action.disabled',
                                        }}
                                    />
                                </Box>
                            )}

                            <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: 600, flex: 1 }}
                            >
                                {index + 1}. {t(`setupGuide.steps.${step.translationKey}.title`)}
                            </Typography>

                            {expandedStep === index ? (
                                <KeyboardArrowUp color="action" />
                            ) : (
                                <KeyboardArrowDown color="action" />
                            )}
                        </Box>

                        <Collapse in={expandedStep === index}>
                            <Box sx={{ px: 2.5, pb: 2.5, pt: 0 }}>
                                {renderStepContent(step)}
                            </Box>
                        </Collapse>
                    </Card>
                ))}
            </Box>
        </Container>
    );
}

export default SetupGuidePage;
